import { RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import FMRAuthGuard from '../components/FMRAuthGuard';
import PortalLayout from '../pages/fmr-portal/components/PortalLayout';

const HomePage = lazy(() => import('../pages/home/page'));
const GalleryPage = lazy(() => import('../pages/gallery/page'));
const VideoGalleryPage = lazy(() => import('../pages/video-gallery/page'));
const WishlistPage = lazy(() => import('../pages/wishlist/page'));
const CheckoutPage = lazy(() => import('../pages/checkout/page'));
const CheckoutSuccess = lazy(() => import('../pages/checkout/components/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('../pages/checkout/components/CheckoutCancel'));
const EventDetailPage = lazy(() => import('../pages/event-detail/page'));
const ServicesPage = lazy(() => import('../pages/services/page'));
const ShopPage = lazy(() => import('../pages/shop/page'));
const InvoicePage = lazy(() => import('../pages/invoice/page'));
const EquipmentHirePage = lazy(() => import('../pages/equipment-hire/page'));
const ReturnChecklistPage = lazy(() => import('../pages/return-checklist/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const ContactPage = lazy(() => import('../pages/contact/page'));

const FMRLoginPage = lazy(() => import('../pages/fmr-portal/login/page'));
const FMRDashboardPage = lazy(() => import('../pages/fmr-portal/dashboard/page'));
const FMRTransactionsPage = lazy(() => import('../pages/fmr-portal/transactions/page'));
const FMRReceiptsPage = lazy(() => import('../pages/fmr-portal/receipts/page'));
const FMREquipmentPage = lazy(() => import('../pages/fmr-portal/equipment/page'));
const FMRStaffPage = lazy(() => import('../pages/fmr-portal/staff/page'));
const FMRBookingsPage = lazy(() => import('../pages/fmr-portal/bookings/page'));
const BankAccountsPage = lazy(() => import('../pages/fmr-portal/bank-accounts/page'));
const FMRReportsPage = lazy(() => import('../pages/fmr-portal/reports/page'));

const PortalFallback = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin"></i>
      <p className="text-slate-400 mt-3 text-sm">Loading portal...</p>
    </div>
  </div>
);

import FMRPortalBookingsPage from '../pages/fmr-portal/bookings/page';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/services',
    element: <ServicesPage />,
  },
  {
    path: '/shop',
    element: <ShopPage />,
  },
  {
    path: '/invoice',
    element: <InvoicePage />,
  },
  {
    path: '/equipment-hire',
    element: <EquipmentHirePage />,
  },
  {
    path: '/return-checklist',
    element: <ReturnChecklistPage />,
  },
  {
    path: '/gallery',
    element: <GalleryPage />,
  },
  {
    path: '/video-gallery',
    element: <VideoGalleryPage />,
  },
  {
    path: '/wishlist',
    element: <WishlistPage />,
  },
  {
    path: '/checkout',
    element: <CheckoutPage />,
  },
  {
    path: '/checkout/success',
    element: <CheckoutSuccess />,
  },
  {
    path: '/checkout/cancel',
    element: <CheckoutCancel />,
  },
  {
    path: '/event/:slug',
    element: <EventDetailPage />,
  },
  {
    path: '/fmr-portal',
    element: <Navigate to="/fmr-portal/login" replace />,
  },
  {
    path: '/fmr-portal/login',
    element: (
      <Suspense fallback={<PortalFallback />}>
        <FMRLoginPage />
      </Suspense>
    ),
  },
  {
    path: '/fmr-portal/dashboard',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <Suspense fallback={<PortalFallback />}>
            <FMRDashboardPage />
          </Suspense>
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/transactions',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <Suspense fallback={<PortalFallback />}>
            <FMRTransactionsPage />
          </Suspense>
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/receipts',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <Suspense fallback={<PortalFallback />}>
            <FMRReceiptsPage />
          </Suspense>
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/equipment',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <Suspense fallback={<PortalFallback />}>
            <FMREquipmentPage />
          </Suspense>
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/staff',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <Suspense fallback={<PortalFallback />}>
            <FMRStaffPage />
          </Suspense>
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/bookings',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <FMRBookingsPage />
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/bank-accounts',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <BankAccountsPage />
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '/fmr-portal/reports',
    element: (
      <FMRAuthGuard>
        <PortalLayout>
          <FMRReportsPage />
        </PortalLayout>
      </FMRAuthGuard>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
